// Page structure: width, header, top bar, sections and the two-column layouts used on a laptop.
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { cx } from '@/lib/cx';
import { IconButton } from './Button';
import { Icon } from './Icon';

/** Reading column (680px) or wide for dashboards (1080px). */
export function Page({ children, wide }: { children: ReactNode; wide?: boolean }) {
  return <div className={cx('page', wide && 'wide')}>{children}</div>;
}

type PageHeaderProps = { title: string; intro?: ReactNode; eyebrow?: ReactNode; aside?: ReactNode };

export function PageHeader({ title, intro, eyebrow, aside }: PageHeaderProps) {
  if (aside) {
    return (
      <header className="between page-head">
        <div>
          {eyebrow && <p className="eyebrow">{eyebrow}</p>}
          <h1>{title}</h1>
          {intro && <p className="t-2">{intro}</p>}
        </div>
        {aside}
      </header>
    );
  }
  return (
    <header className="page-head">
      {eyebrow && <p className="eyebrow">{eyebrow}</p>}
      <h1>{title}</h1>
      {intro && <p>{intro}</p>}
    </header>
  );
}

/** Back link on the left, anything on the right. */
export function TopBar({ back, right }: { back?: { to: string; label: string }; right?: ReactNode }) {
  return (
    <div className="topbar">
      {back ? <BackLink to={back.to} label={back.label} /> : <span />}
      {right}
    </div>
  );
}

export function BackLink({ to, label }: { to: string; label: string }) {
  return (
    <Link className="back" to={to}>
      <Icon name="chevron-left" />
      {label}
    </Link>
  );
}

type FlowBarProps = {
  /** Where the close button goes. Without it there is no close button (e.g. while scoring). */
  exitTo?: string;
  exitLabel?: string;
  /** Center: a chip with the step or state. */
  center: ReactNode;
  right?: ReactNode;
};

/** Top of an exercise in focus mode: close on the left, the step in the middle. */
export function FlowBar({ exitTo, exitLabel = 'Close', center, right }: FlowBarProps) {
  return (
    <div className="topbar">
      {exitTo ? <IconButton icon="x" label={exitLabel} to={exitTo} /> : <span style={{ width: 'var(--tap)' }} />}
      {center}
      {right ?? <span style={{ width: 'var(--tap)' }} />}
    </div>
  );
}

/** Thin bar across the top of an exercise. value: 0 to 1. */
export function ProgressTop({ value, label }: { value: number; label?: string }) {
  return (
    <div
      className="progress-top"
      role="progressbar"
      aria-label={label ?? 'Progress'}
      aria-valuenow={Math.round(value * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <span style={{ width: `${Math.round(value * 100)}%` }} />
    </div>
  );
}

type SectionTitleProps = { title: string; aside?: ReactNode; level?: 2 | 3 };

/** Heading above a list or group, with an optional link or count on the right. */
export function SectionTitle({ title, aside }: SectionTitleProps) {
  return (
    <div className="section-title">
      <h2>
        <span className="highlight">{title}</span>
      </h2>
      {aside}
    </div>
  );
}

/** Small gray heading that groups cards (Practice). */
export function GroupTitle({ children }: { children: ReactNode }) {
  return <h2 className="group-title">{children}</h2>;
}

/** Laptop: the task on the left, context (trend, tips) in a 320px column on the right. Phone: stacked. */
export function Split({ main, aside }: { main: ReactNode; aside: ReactNode }) {
  return (
    <div className="split">
      <section className="stack">{main}</section>
      <aside className="stack">{aside}</aside>
    </div>
  );
}

/** Results: the summary (sticky on a laptop) beside the details. */
export function ResultLayout({ summary, details }: { summary: ReactNode; details: ReactNode }) {
  return (
    <div className="result">
      <section className="summary stack">{summary}</section>
      <section className="stack">{details}</section>
    </div>
  );
}

/** The main action of an exercise, pinned to the bottom on a phone. `pin` keeps it above the tab bar. */
export function Dock({ children, pin }: { children: ReactNode; pin?: boolean }) {
  return <div className={cx('dock', pin && 'pin')}>{children}</div>;
}
